<template>
  <div class="social__icon" v-if="iconExists">
    <component :is="iconComponent" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, defineAsyncComponent, ref, onMounted, type Component } from 'vue'

export default defineComponent({
  name: 'SocialIcon',
  props: {
    url: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const iconExists = ref(false)
    const iconComponent = ref<Component | null>(null)

    // Extract platform from URL
    const platform = computed(() => {
      if (!props.url) return null
      
      const url = props.url.toLowerCase()
      
      // Map common domains to platform names
      if (url.includes('facebook.com') || url.includes('fb.com')) return 'facebook'
      if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter'
      if (url.includes('instagram.com')) return 'instagram'
      if (url.includes('linkedin.com')) return 'linkedin'
      if (url.includes('medium.com')) return 'medium'
      if (url.includes('bsky.app') || url.includes('bluesky.social')) return 'bluesky'
      if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube'
      if (url.includes('tiktok.com')) return 'tiktok'
      if (url.includes('github.com')) return 'github'
      if (url.includes('discord.gg') || url.includes('discord.com')) return 'discord'
      if (url.includes('twitch.tv')) return 'twitch'
      if (url.includes('reddit.com')) return 'reddit'
      if (url.includes('snapchat.com')) return 'snapchat'
      if (url.includes('pinterest.com')) return 'pinterest'
      if (url.includes('whatsapp.com')) return 'whatsapp'
      if (url.includes('telegram.org') || url.includes('t.me')) return 'telegram'
      
      return null
    })

    // Load the icon component dynamically
    const loadIcon = async () => {
      if (!platform.value) return
      
      try {
        // Map platform names to exact component file names (all lowercase)
        const componentMap: Record<string, string> = {
          'facebook': 'facebook',
          'twitter': 'twitter', 
          'instagram': 'instagram',
          'linkedin': 'linkedin',
          'medium': 'medium',
          'bluesky': 'bluesky'
        }
        
        const componentName = componentMap[platform.value]
        if (!componentName) return
        
        // Use explicit imports to avoid Vite dynamic import restrictions
        switch (componentName) {
          case 'facebook':
            iconComponent.value = defineAsyncComponent(() => import('./facebook.vue'))
            break
          case 'twitter':
            iconComponent.value = defineAsyncComponent(() => import('./twitter.vue'))
            break
          case 'instagram':
            iconComponent.value = defineAsyncComponent(() => import('./instagram.vue'))
            break
          case 'linkedin':
            iconComponent.value = defineAsyncComponent(() => import('./linkedin.vue'))
            break
          case 'medium':
            iconComponent.value = defineAsyncComponent(() => import('./medium.vue'))
            break
          case 'bluesky':
            iconComponent.value = defineAsyncComponent(() => import('./bluesky.vue'))
            break
          default:
            return
        }
        iconExists.value = true
      } catch (error) {
        iconExists.value = false
      }
    }

    onMounted(() => {
      loadIcon()
    })

    return {
      platform,
      iconComponent,
      iconExists
    }
  }
})
</script>